import { useEffect, useRef, type RefObject } from 'react';
import { useLocation, useParams } from 'react-router';

/**
 * Переход на другую страницу -- как в макете (обработчик hashchange в
 * docs/design/frontend-v2/prototype.js): прокрутка к началу и фокус в содержимом
 * без прокрутки к нему. Первое открытие фокус не трогает: первым по Tab остаётся
 * ссылка «К содержимому».
 *
 * Параметры поиска (фильтры, поиск в адресе) страницу не меняют. Не меняет её и
 * вкладка в адресе: элемент, на котором стоял фокус, пережил переход и остался в
 * содержимом -- фокус и прокрутка остаются на месте, как у вкладок макета.
 */
export function useRouteChangeEffects(mainRef: RefObject<HTMLElement | null>) {
	const { pathname } = useLocation();
	/* Идентификаторы объектов меняют страницу; параметры вкладки и направления — нет */
	const {
		tab: _tab,
		detailsWith: _detailsWith,
		flowDirection: _flowDirection,
		...identityParams
	} = useParams();
	const identity = JSON.stringify(identityParams);
	const previous = useRef({ pathname, identity });

	useEffect(() => {
		if (previous.current.pathname === pathname) return;
		const identityChanged = previous.current.identity !== identity;
		previous.current = { pathname, identity };

		const main = mainRef.current;
		if (!main) return;
		const focused = document.activeElement;
		if (
			!identityChanged &&
			focused !== main &&
			focused instanceof HTMLElement &&
			main.contains(focused)
		)
			return;

		window.scrollTo(0, 0);
		main.focus({ preventScroll: true });
	}, [pathname, identity, mainRef]);
}
